/**
 * Helpers for the "trusted device" mechanism that skips MFA / email-code
 * verification for 15 days after the user has successfully signed in once
 * from this browser.
 *
 * The token itself is issued and validated by the backend (a JWT signed with
 * the server secret, expiring in 15 days). The frontend only stores it scoped
 * by username so the right token is sent for the right account.
 */

const STORAGE_KEY = 'trustedDevices';
// Mirror the backend lifetime so we don't attempt to use a token that the
// server will reject anyway. The backend remains the source of truth.
export const TRUSTED_DEVICE_TTL_MS = 15 * 24 * 60 * 60 * 1000;

type StoredEntry = { token: string; expiresAt: number };
type Store = Record<string, StoredEntry>;

const normalizeUsername = (username: string): string => (username || '').trim().toLowerCase();

const readStore = (): Store => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Store) : {};
  } catch {
    return {};
  }
};

const writeStore = (store: Store): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore quota / serialization errors – worst case the user just gets
    // asked for MFA again on the next login.
  }
};

export const getTrustedDeviceToken = (username: string): string | null => {
  const key = normalizeUsername(username);
  if (!key) return null;

  const store = readStore();
  const entry = store[key];
  if (!entry || !entry.token) return null;

  if (!entry.expiresAt || entry.expiresAt < Date.now()) {
    delete store[key];
    writeStore(store);
    return null;
  }

  return entry.token;
};

export const saveTrustedDeviceToken = (username: string, token: string): void => {
  const key = normalizeUsername(username);
  if (!key || !token) return;

  const store = readStore();
  store[key] = {
    token,
    expiresAt: Date.now() + TRUSTED_DEVICE_TTL_MS,
  };
  writeStore(store);
};

export const clearTrustedDeviceToken = (username: string): void => {
  const key = normalizeUsername(username);
  if (!key) return;

  const store = readStore();
  if (store[key]) {
    delete store[key];
    writeStore(store);
  }
};
